import React, { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/**
 * API timestamps are ISO strings with a trailing "Z" (UTC). Parse via Date so
 * tooltips show a local string instead of the raw wire format.
 */
function formatShareTimestamp(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

function resolveGroupShare(post) {
  const top = post?.group_share;
  if (top && typeof top === 'object') return top;
  const nested = post?.platform_specific?.facebook?.group_share;
  return nested && typeof nested === 'object' ? nested : null;
}

/** Compact table cells: never shrink — Status row used minWidth:0 and hid the chip. */
const rootSx = (compact, extra = {}) => ({
  flexShrink: 0,
  minWidth: compact ? 'max-content' : 180,
  alignSelf: 'flex-start',
  ...extra,
});

/**
 * Phase 18.2-ADD-B: "Groups: 3/5 shared" summary with expandable per-group detail.
 */
export default function GroupShareStatus({ post, compact = false }) {
  const [open, setOpen] = useState(false);
  const gs = resolveGroupShare(post);
  if (!gs || typeof gs !== 'object') return null;

  const jobStatus = String(gs.status || '').toLowerCase();
  const ok = Number(gs.ok_count || 0);
  const failed = Number(gs.failed_count || 0);
  const total =
    Number(gs.total || 0) ||
    ok + failed ||
    (Array.isArray(gs.results) ? gs.results.length : 0);

  if (jobStatus === 'queued') {
    const queuedLabel = formatShareTimestamp(gs.queued_at);
    return (
      <Box sx={rootSx(compact)}>
        <Tooltip title={queuedLabel ? `Queued ${queuedLabel}` : 'Queued for group share'}>
          <Chip size="small" color="info" variant="outlined" label="Groups: queued" />
        </Tooltip>
      </Box>
    );
  }

  if (jobStatus === 'sharing') {
    return (
      <Box sx={rootSx(compact, { display: 'flex', alignItems: 'center', gap: 0.75 })}>
        <CircularProgress size={14} />
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label={gs.attempt > 1 ? `Groups: sharing (try ${gs.attempt})` : 'Groups: sharing…'}
        />
      </Box>
    );
  }

  if (jobStatus === 'skipped') {
    return (
      <Box sx={rootSx(compact)}>
        <Tooltip title={gs.error || 'Auto-share skipped'}>
          <Chip size="small" color="default" variant="outlined" label="Groups: skipped" />
        </Tooltip>
      </Box>
    );
  }

  if (total === 0 && !gs.error && !(gs.results || []).length) return null;

  const allOk = total > 0 && failed === 0 && ok === total;
  const chipColor = allOk ? 'success' : ok > 0 ? 'warning' : 'error';
  const label =
    total > 0 ? `Groups: ${ok}/${total} shared` : 'Groups: share failed';

  const results = Array.isArray(gs.results) ? gs.results : [];
  const attemptedLabel = formatShareTimestamp(gs.attempted_at);

  return (
    <Box sx={rootSx(compact)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip
          title={
            attemptedLabel
              ? `Last attempt ${attemptedLabel}${gs.error ? ` — ${gs.error}` : ''}`
              : gs.error || 'Group share status'
          }
        >
          <Chip
            size="small"
            color={chipColor}
            variant={allOk ? 'filled' : 'outlined'}
            label={label}
            onClick={() => setOpen((v) => !v)}
            sx={{ cursor: 'pointer' }}
          />
        </Tooltip>
        {results.length > 0 && (
          <IconButton
            size="small"
            aria-label={open ? 'Hide group details' : 'Show group details'}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>
      <Collapse in={open}>
        <List dense disablePadding sx={{ mt: 0.5, maxWidth: 420 }}>
          {results.map((row, idx) => {
            if (!row || typeof row !== 'object') return null;
            const status = row.status || (row.success ? 'success' : 'failed');
            const reason =
              row.reason_label ||
              (row.reason
                ? String(row.reason).replace(/_/g, ' ')
                : null);
            const secondary = [
              status,
              reason,
              row.error && status !== 'success' ? row.error : null,
            ]
              .filter(Boolean)
              .join(' — ');
            return (
              <ListItem key={`${row.group_id}-${idx}`} sx={{ py: 0.25, px: 0 }}>
                <ListItemText
                  primary={row.group_id || 'Unknown group'}
                  secondary={secondary}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color:
                      status === 'success'
                        ? 'success.main'
                        : status === 'skipped'
                          ? 'text.secondary'
                          : 'error.main',
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    color: 'text.secondary',
                  }}
                />
              </ListItem>
            );
          })}
          {!results.length && gs.error && (
            <Typography variant="caption" color="error">
              {gs.error}
            </Typography>
          )}
        </List>
      </Collapse>
    </Box>
  );
}
